import { IS_SSR } from '@libs/common-next';
import { logger } from '@libs/core';

import { type FormFields, formSchema } from '@/entities/purchase-form';

export const PENDING_FAVORITE_KEY = 'pendingFavorite';

export type PendingFavoriteSelections = Pick<
    FormFields,
    'quantity' | 'purchaseOption' | 'autoshipFrequency' | 'autoshipUnit' | 'fulfilmentMethod'
> &
    Partial<Pick<FormFields, 'personalization'>>;

type PendingFavoriteTarget = {
    webStyleId: string;
    favoriteSku: string;
};

export type PendingFavorite = PendingFavoriteTarget & {
    selections?: PendingFavoriteSelections;
};

const pendingFavoritePurchaseSelectionsSchema = formSchema.pick({
    quantity: true,
    purchaseOption: true,
    autoshipFrequency: true,
    autoshipUnit: true,
    fulfilmentMethod: true,
});

const isPendingFavoriteTarget = (value: unknown): value is PendingFavoriteTarget => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;

    const pending = value as Partial<PendingFavoriteTarget>;

    return typeof pending.webStyleId === 'string' && typeof pending.favoriteSku === 'string';
};

const isPendingFavoritePersonalization = (value: unknown): value is PendingFavoriteSelections['personalization'] => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const setPendingFavorite = (pending: PendingFavorite) => {
    if (IS_SSR) return;

    try {
        sessionStorage.setItem(PENDING_FAVORITE_KEY, JSON.stringify(pending));
    } catch (error) {
        logger.error('[PENDING FAVORITE][Error]: Failed to write to sessionStorage', error);
    }
};

export const readPendingFavorite = () => {
    if (IS_SSR) return undefined;

    try {
        const raw = sessionStorage.getItem(PENDING_FAVORITE_KEY);
        if (!raw) return undefined;

        return JSON.parse(raw) as unknown;
    } catch (error) {
        logger.error('[PENDING FAVORITE][Error]: Failed to read from sessionStorage', error);
        return undefined;
    }
};

export const consumePendingFavorite = (webStyleId: string): PendingFavorite | null => {
    try {
        const parsed = readPendingFavorite();

        if (parsed === undefined) return null;

        if (!isPendingFavoriteTarget(parsed)) {
            sessionStorage.removeItem(PENDING_FAVORITE_KEY);
            return null;
        }

        if (parsed.webStyleId !== webStyleId) return null;

        sessionStorage.removeItem(PENDING_FAVORITE_KEY);

        const pending = parsed as PendingFavorite;
        const purchaseSelectionsResult = pendingFavoritePurchaseSelectionsSchema.safeParse(pending.selections);

        if (!purchaseSelectionsResult.success) {
            return {
                webStyleId: pending.webStyleId,
                favoriteSku: pending.favoriteSku,
            };
        }

        const personalization = pending.selections?.personalization;
        const selections = isPendingFavoritePersonalization(personalization)
            ? { ...purchaseSelectionsResult.data, personalization }
            : purchaseSelectionsResult.data;

        return {
            webStyleId: pending.webStyleId,
            favoriteSku: pending.favoriteSku,
            selections,
        };
    } catch (error) {
        logger.error('[PENDING FAVORITE][Error]: Failed to consume from sessionStorage', error);
        return null;
    }
};
